import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../contexts/UserContext'
import { StakeholderType, UserType } from '../../types/user'
import NotificationBadge from '../NotificationBadge'
import ProfileSwitcher from '../ProfileSwitcher'

const Header: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState<boolean>(false)
  const [projectsOpen, setProjectsOpen] = useState<boolean>(false)
  const [workOrdersOpen, setWorkOrdersOpen] = useState<boolean>(false)
  const [invoicesOpen, setInvoicesOpen] = useState<boolean>(false)
  const [statusReportsOpen, setStatusReportsOpen] = useState<boolean>(false)

  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useUser()
  const currentProfile = user?.current_profile?.stakeholder_type
  const isDriver = user?.user_type === 'DRIVER'
  const isBroker = currentProfile === StakeholderType.BROKER
  const isContractor = currentProfile === StakeholderType.CONTRACTOR
  const isSubhauler = currentProfile === StakeholderType.SUB_HAULER
  const isSiteSupervisor = user?.user_type === UserType.SITE_SUPERVISOR
  const isDispatcher = user?.user_type === UserType.DISPATCHER
  const canViewDispatches = isBroker || isSubhauler || isContractor

  const { tags, projects, workOrders } = useNotificationContext()

  useEffect(() => {
    if (location.pathname.includes('tags')) setTagsOpen(true)
    if (location.pathname.includes('projects')) setProjectsOpen(true)
    if (location.pathname.includes('work-orders')) setWorkOrdersOpen(true)
    if (location.pathname.includes('invoices')) setInvoicesOpen(true)
    if (location.pathname.includes('status-reports')) setStatusReportsOpen(true)
  }, [location.pathname])

  const closeDrawer = () => setDrawerOpen(false)

  const handleKeyToggle = (e: React.KeyboardEvent, toggle: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
    closeDrawer()
  }

  const NavItem = ({ children }: { children: React.ReactNode }) => (
    <div className={css(styles.navItem)}>{children}</div>
  )

  return (
    <div className={css(headerStyles.navbar)}>
      <div className={css(headerStyles.navbarContent)}>
        <div className={css(headerStyles.logo)}>
          <button
            className={css(headerStyles.menuButton)}
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            onKeyDown={(e) => handleKeyToggle(e, () => setDrawerOpen(true))}
          >
            <i className="fa-solid fa-bars" />
          </button>
        </div>
      </div>
      <div className={css(headerStyles.divider)} />

      {drawerOpen && (
        <div
          className={css(styles.overlay)}
          onClick={closeDrawer}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => handleKeyToggle(e, closeDrawer)}
        />
      )}

      <div
        className={css(
          styles.drawer,
          drawerOpen ? styles.drawerOpen : styles.drawerClosed,
        )}
      >
        <div className={css(styles.drawerHeader)}>
          <div className={css(styles.drawerTitle)}>Menu</div>
          <button
            className={css(styles.closeButton)}
            aria-label="Close menu"
            onClick={closeDrawer}
            onKeyDown={(e) => handleKeyToggle(e, closeDrawer)}
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        <div className={css(styles.menu)}>
          {!isDriver && !isSubhauler && (
            <NavItem>
              <div className={css(styles.itemRow)}>
                <Link
                  to="/projects/pending"
                  className={css(styles.link)}
                  onClick={closeDrawer}
                >
                  <span className={css(styles.iconCell)}>
                    <i className={`fa-solid fa-folder ${css(styles.icon)}`} />
                  </span>
                  <span>Project Terms</span>
                </Link>
                {(isBroker || isContractor || isSubhauler) && (
                  <NotificationBadge count={projects.unreadCount} size="md" />
                )}
                <button
                  className={css(styles.chevronBtn)}
                  onClick={() => setProjectsOpen(!projectsOpen)}
                  onKeyDown={(e) =>
                    handleKeyToggle(e, () => setProjectsOpen(!projectsOpen))
                  }
                  aria-label="Toggle projects submenu"
                >
                  {projectsOpen ? (
                    <i className="fa-solid fa-chevron-up" />
                  ) : (
                    <i className="fa-solid fa-chevron-down" />
                  )}
                </button>
              </div>
              {projectsOpen && (
                <div className={css(styles.submenu)}>
                  <Link
                    to="/projects/pending"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/projects/pending' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Pending
                    {(isBroker || isContractor || isSubhauler) && (
                      <NotificationBadge
                        count={projects.unreadCount}
                        size="sm"
                      />
                    )}
                  </Link>
                  <Link
                    to="/projects/accepted"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/projects/accepted' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Accepted
                  </Link>
                  <Link
                    to="/projects/declined"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/projects/declined' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Declined
                  </Link>
                  <Link
                    to="/projects/drafts"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/projects/drafts' && styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Drafts
                  </Link>
                </div>
              )}
            </NavItem>
          )}

          {!isDriver && canViewDispatches && (
            <NavItem>
              <div className={css(styles.itemRow)}>
                <Link
                  to="/work-orders/pending"
                  className={css(styles.link)}
                  onClick={closeDrawer}
                >
                  <span className={css(styles.iconCell)}>
                    <i
                      className={`fa-solid fa-file-lines ${css(styles.icon)}`}
                    />
                  </span>
                  <span>Dispatches</span>
                </Link>
                {canViewDispatches && (
                  <NotificationBadge count={workOrders.unreadCount} size="md" />
                )}
                <button
                  className={css(styles.chevronBtn)}
                  onClick={() => setWorkOrdersOpen(!workOrdersOpen)}
                  onKeyDown={(e) =>
                    handleKeyToggle(e, () => setWorkOrdersOpen(!workOrdersOpen))
                  }
                  aria-label="Toggle work orders submenu"
                >
                  {workOrdersOpen ? (
                    <i className="fa-solid fa-chevron-up" />
                  ) : (
                    <i className="fa-solid fa-chevron-down" />
                  )}
                </button>
              </div>
              {workOrdersOpen && (
                <div className={css(styles.submenu)}>
                  <Link
                    to="/work-orders/pending"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/work-orders/pending' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Pending
                    {canViewDispatches && (
                      <NotificationBadge
                        count={workOrders.pendingCount}
                        size="sm"
                      />
                    )}
                  </Link>
                  <Link
                    to="/work-orders/accepted"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/work-orders/accepted' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Accepted
                    {canViewDispatches && (
                      <NotificationBadge
                        count={workOrders.acceptedCount}
                        size="sm"
                      />
                    )}
                  </Link>
                  <Link
                    to="/work-orders/completed"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/work-orders/completed' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Completed
                  </Link>
                  <Link
                    to="/work-orders/declined"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/work-orders/declined' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Declined
                  </Link>
                </div>
              )}
            </NavItem>
          )}

          <NavItem>
            <div className={css(styles.itemRow)}>
              <Link
                to="/tags/open"
                className={css(styles.link)}
                onClick={closeDrawer}
              >
                <span className={css(styles.iconCell)}>
                  <i className={`fa-solid fa-tags ${css(styles.icon)}`} />
                </span>
                <span>Tags</span>
              </Link>
              {isDriver && (
                <NotificationBadge count={tags.unreadCount} size="md" />
              )}
              <button
                className={css(styles.chevronBtn)}
                onClick={() => setTagsOpen(!tagsOpen)}
                onKeyDown={(e) =>
                  handleKeyToggle(e, () => setTagsOpen(!tagsOpen))
                }
                aria-label="Toggle tags submenu"
              >
                {tagsOpen ? (
                  <i className="fa-solid fa-chevron-up" />
                ) : (
                  <i className="fa-solid fa-chevron-down" />
                )}
              </button>
            </div>
            {tagsOpen && (
              <div className={css(styles.submenu)}>
                <Link
                  to="/tags/open"
                  className={css(
                    styles.submenuLink,
                    location.pathname === '/tags/open' && styles.active,
                  )}
                  onClick={closeDrawer}
                >
                  Open Tags
                  {isDriver && (
                    <NotificationBadge count={tags.unreadCount} size="sm" />
                  )}
                </Link>
                <Link
                  to="/tags/completed"
                  className={css(
                    styles.submenuLink,
                    location.pathname === '/tags/completed' && styles.active,
                  )}
                  onClick={closeDrawer}
                >
                  Completed Tags
                </Link>
              </div>
            )}
          </NavItem>

          <NavItem>
            <div className={css(styles.itemRow)}>
              <Link
                to="/contacts"
                className={css(styles.link)}
                onClick={closeDrawer}
              >
                <span className={css(styles.iconCell)}>
                  <i
                    className={`fa-solid fa-address-book ${css(styles.icon)}`}
                  />
                </span>
                <span>Directory & Invites</span>
              </Link>
            </div>
          </NavItem>

          {!isDriver && (
            <NavItem>
              <div className={css(styles.itemRow)}>
                <Link
                  to="/trucks"
                  className={css(styles.link)}
                  onClick={closeDrawer}
                >
                  <span className={css(styles.iconCell)}>
                    <i className={`fa-solid fa-truck ${css(styles.icon)}`} />
                  </span>
                  <span>Trucks</span>
                </Link>
              </div>
            </NavItem>
          )}

          {!isDriver && !isSiteSupervisor && !isDispatcher && (
            <NavItem>
              <div className={css(styles.itemRow)}>
                <Link
                  to="/invoices/sent"
                  className={css(styles.link)}
                  onClick={closeDrawer}
                >
                  <span className={css(styles.iconCell)}>
                    <i
                      className={`fa-solid fa-file-invoice ${css(styles.icon)}`}
                    />
                  </span>
                  <span>Invoices</span>
                </Link>
                <button
                  className={css(styles.chevronBtn)}
                  onClick={() => setInvoicesOpen(!invoicesOpen)}
                  onKeyDown={(e) =>
                    handleKeyToggle(e, () => setInvoicesOpen(!invoicesOpen))
                  }
                  aria-label="Toggle invoices submenu"
                >
                  {invoicesOpen ? (
                    <i className="fa-solid fa-chevron-up" />
                  ) : (
                    <i className="fa-solid fa-chevron-down" />
                  )}
                </button>
              </div>
              {invoicesOpen && (
                <div className={css(styles.submenu)}>
                  <Link
                    to="/invoices/sent"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/invoices/sent' && styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Accounts Receivable
                  </Link>
                  <Link
                    to="/invoices/received"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/invoices/received' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Accounts Payable
                  </Link>
                </div>
              )}
            </NavItem>
          )}

          {!isDriver && (
            <NavItem>
              <div className={css(styles.itemRow)}>
                <Link
                  to="/status-reports/sent"
                  className={css(styles.link)}
                  onClick={closeDrawer}
                >
                  <span className={css(styles.iconCell)}>
                    <i
                      className={`fa-solid fa-chart-line ${css(styles.icon)}`}
                    />
                  </span>
                  <span>Status Reports</span>
                </Link>
                <button
                  className={css(styles.chevronBtn)}
                  onClick={() => setStatusReportsOpen(!statusReportsOpen)}
                  onKeyDown={(e) =>
                    handleKeyToggle(e, () =>
                      setStatusReportsOpen(!statusReportsOpen),
                    )
                  }
                  aria-label="Toggle status reports submenu"
                >
                  {statusReportsOpen ? (
                    <i className="fa-solid fa-chevron-up" />
                  ) : (
                    <i className="fa-solid fa-chevron-down" />
                  )}
                </button>
              </div>
              {statusReportsOpen && (
                <div className={css(styles.submenu)}>
                  <Link
                    to="/status-reports/sent"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/status-reports/sent' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Sent
                  </Link>
                  <Link
                    to="/status-reports/received"
                    className={css(
                      styles.submenuLink,
                      location.pathname === '/status-reports/received' &&
                        styles.active,
                    )}
                    onClick={closeDrawer}
                  >
                    Received
                  </Link>
                </div>
              )}
            </NavItem>
          )}

          <NavItem>
            <div className={css(styles.itemRow)}>
              <Link
                to="/profile"
                className={css(styles.link)}
                onClick={closeDrawer}
              >
                <span className={css(styles.iconCell)}>
                  <i className={`fa-solid fa-cog ${css(styles.icon)}`} />
                </span>
                <span>Settings</span>
              </Link>
            </div>
          </NavItem>
        </div>

        <div className={css(styles.logoutSection)}>
          <button onClick={handleLogout} className={css(styles.logoutButton)}>
            <i className="fa-solid fa-sign-out-alt" />
            <span>Logout</span>
          </button>
        </div>

        <div className={css(styles.footer)}>
          <ProfileSwitcher />
        </div>
      </div>
    </div>
  )
}

const headerStyles = StyleSheet.create({
  headerSection: {
    height: '157px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  navbar: {
    width: '100%',
    height: '64px',
    background: '#1F2A37',
  },
  navbarContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0 16px',
    height: '63px',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: 'auto',
    height: '32px',
  },
  menuButton: {
    width: '32px',
    height: '32px',
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: 0,
  },
  rightContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingLeft: '16px',
    gap: '12px',
    width: '278px',
    height: '32px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    background: '#F3F4F6',
  },
  divider: {
    width: '100%',
    height: '1px',
    background: '#4B5563',
  },
  heading: {
    padding: '16px',
    background: '#FFFFFF',
    width: '100%',
    height: '93px',
  },
})

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.4)',
    zIndex: 1000,
  },
  drawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: 280,
    background: '#1F2A37',
    color: '#fff',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(-100%)',
    transition: 'transform 200ms ease-out',
  },
  drawerOpen: {
    transform: 'translateX(0)',
  },
  drawerClosed: {
    transform: 'translateX(-100%)',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  drawerTitle: {
    fontWeight: 600,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    fontSize: 16,
  },
  menu: {
    flex: 1,
    padding: '8px 8px 0 8px',
    overflowY: 'auto',
  },
  navItem: {
    padding: 8,
    borderRadius: 4,
  },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    alignItems: 'center',
    gap: 8,
    '@media (max-width: 480px)': {
      gap: 4,
    },
  },
  link: {
    color: '#fff',
    textDecoration: 'none',
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    alignItems: 'center',
    gap: 8,
  },
  iconCell: {
    width: 24,
    display: 'flex',
    justifyContent: 'center',
  },
  icon: {
    opacity: 0.7,
  },
  chevronBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: 8,
  },
  submenu: {
    paddingTop: 8,
    paddingLeft: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    '@media (max-width: 480px)': {
      paddingLeft: 16,
      gap: 4,
    },
  },
  submenuLink: {
    color: '#fff',
    textDecoration: 'none',
    padding: 8,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ':hover': {
      background: '#374151',
    },
    '@media (max-width: 480px)': {
      padding: 6,
      fontSize: '14px',
    },
  },
  active: {
    background: '#374151',
  },
  logoutSection: {
    padding: 8,
  },
  footer: {
    padding: 0,
  },
  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    width: '100%',
    justifyContent: 'flex-start',
    ':hover': {
      color: '#fff',
      backgroundColor: '#374151',
    },
  },
})

export default Header
